import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Typography, Tooltip, Box } from '@mui/material';
import { getUsers } from '../../services/userService';

// Custom mention chip styled to match edit mode exactly
const MentionChipContainer = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  verticalAlign: 'middle',
  backgroundColor: theme.palette.mode === 'light' 
    ? theme.palette.primary.main 
    : theme.palette.primary.light,
  color: theme.palette.mode === 'light'
    ? theme.palette.primary.contrastText
    : 'white',
  borderRadius: '16px',
  padding: '2px 8px 2px 4px',
  margin: '0 2px',
  fontSize: '0.8125rem',
  whiteSpace: 'nowrap',
  position: 'relative',
  gap: '4px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light'
      ? theme.palette.primary.dark
      : theme.palette.primary.main,
  }
}));

const MentionAvatar = styled('span')(({ theme }) => ({
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  backgroundColor: theme.palette.mode === 'light'
    ? theme.palette.primary.dark
    : theme.palette.primary.dark,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '10px',
  fontWeight: 'bold',
  color: theme.palette.mode === 'light'
    ? theme.palette.primary.contrastText
    : 'white',
  flexShrink: 0,
}));

const MentionLabel = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
}));

/**
 * Regex to match @mentions in text
 * Matches @username format and captures the username part
 */
const MENTION_REGEX = /@(\w+(?:[-_]\w+)*)/g;

/**
 * MentionText component
 * Renders text with @mentions highlighted and interactive
 * 
 * @param {Object} props
 * @param {string} props.text - The text content with potential @mentions
 * @param {Object} props.users - Optional pre-loaded user objects (to avoid fetching)
 * @param {Function} props.onMentionClick - Function called when a mention is clicked
 * @param {Object} props.typographyProps - Props to pass to the Typography component
 */
const MentionText = ({ 
  text, 
  users = {}, 
  onMentionClick,
  typographyProps = {},
  variant,
  sx = {}
}) => {
  const [formattedParts, setFormattedParts] = useState([]);
  const [mentionedUsers, setMentionedUsers] = useState({...users});
  const [loading, setLoading] = useState(false);
  const fetchedUsernamesRef = useRef(new Set());
  
  // Parse text and format mentions
  useEffect(() => {
    if (!text) {
      setFormattedParts([]);
      // Reset fetch tracking when text changes
      fetchedUsernamesRef.current.clear();
      return;
    }
    
    // Check if text contains HTML list tags - if so, treat it differently
    if (text.includes('<ul>') || text.includes('<ol>') || text.includes('<li>')) {
      // For HTML content with lists, we'll render it directly with dangerouslySetInnerHTML
      setFormattedParts([{ type: 'html-content', content: text }]);
      return;
    }
    
    // Decode HTML entities in the text first
    // Handle double-encoded entities first
    let decodedText = text
      .replace(/&amp;nbsp;/g, ' ')
      .replace(/&amp;lt;/g, '<')
      .replace(/&amp;gt;/g, '>')
      .replace(/&amp;quot;/g, '"')
      .replace(/&amp;#39;/g, "'")
      .replace(/&amp;amp;/g, '&');
      
    // Then handle single-encoded entities
    decodedText = decodedText
      .replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
    
    // Extract mentions from text
    const mentions = [];
    const mentionMatches = [...decodedText.matchAll(MENTION_REGEX)];
    
    mentionMatches.forEach(match => {
      const username = match[1];
      mentions.push({
        username,
        startIndex: match.index,
        endIndex: match.index + match[0].length,
      });
    });
    
    // If no mentions found, just return the text as a single part
    if (mentions.length === 0) {
      setFormattedParts([{ type: 'text', content: decodedText }]);
      return;
    }
    
    // Create parts array with regular text and mentions
    const parts = [];
    let lastIndex = 0;
    
    mentions.forEach(mention => {
      // Add text before the mention
      if (mention.startIndex > lastIndex) {
        parts.push({
          type: 'text',
          content: decodedText.substring(lastIndex, mention.startIndex),
        });
      }
      
      // Add the mention
      parts.push({
        type: 'mention',
        content: decodedText.substring(mention.startIndex, mention.endIndex),
        username: mention.username,
      });
      
      lastIndex = mention.endIndex;
    });
    
    // Add any remaining text after the last mention
    if (lastIndex < decodedText.length) {
      parts.push({
        type: 'text',
        content: decodedText.substring(lastIndex),
      });
    }
    
    setFormattedParts(parts);
    
    // Fetch user data for mentions if not already loaded or being fetched
    const usernamesToFetch = mentions
      .map(m => m.username)
      .filter(username => !mentionedUsers[username] && !fetchedUsernamesRef.current.has(username));
    
    if (usernamesToFetch.length > 0) {
      fetchUsersByUsername(usernamesToFetch);
    }
  }, [text]); // Remove mentionedUsers from dependencies to prevent infinite loop
  
  // Fetch user data for mentions
  const fetchUsersByUsername = async (usernames) => {
    if (!usernames.length) return;
    
    // Mark these usernames as being fetched to prevent duplicate requests
    usernames.forEach(username => fetchedUsernamesRef.current.add(username));
    
    setLoading(true);
    try {
      // In a real implementation, you'd want to batch these requests
      // For simplicity, we're fetching them one by one here
      const fetchedUsers = {};
      
      await Promise.all(usernames.map(async (username) => {
        try {
          // Parse the username format (firstName_lastName)
          const nameParts = username.split('_');
          if (nameParts.length >= 2) {
            const firstName = nameParts[0];
            const lastName = nameParts.slice(1).join(' ');
            
            // Search by first name and last name
            const response = await getUsers({ 
              search: `${firstName} ${lastName}`,
              limit: 1
            });
            
            if (response.data && response.data.length > 0) {
              fetchedUsers[username] = response.data[0];
            }
          }
        } catch (err) {
          console.error(`Error fetching user data for ${username}:`, err);
          // Remove from fetched set on error so we can retry
          fetchedUsernamesRef.current.delete(username);
        }
      }));
      
      setMentionedUsers(prev => ({
        ...prev,
        ...fetchedUsers
      }));
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Remove all usernames from fetched set on general error
      usernames.forEach(username => fetchedUsernamesRef.current.delete(username));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle mention click
  const handleMentionClick = (username) => {
    if (onMentionClick && mentionedUsers[username]) {
      onMentionClick(mentionedUsers[username]);
    }
  };
  
  // Render text with mentions
  return (
    <Box 
      component="div" 
      sx={{ 
        whiteSpace: 'pre-wrap', 
        wordBreak: 'break-word',
        lineHeight: '1.4375em',
        ...sx 
      }}
      {...typographyProps}
    >
      {formattedParts.map((part, index) => {
        if (part.type === 'html-content') {
          // Render HTML content with lists and mentions
          let processedHtml = part.content;
          
          // Convert mentions to styled spans
          const mentionPattern = /@(\w+)_(\w+)/g;
          processedHtml = processedHtml.replace(mentionPattern, (match, firstName, lastName) => {
            const initials = `${firstName[0]}${lastName[0]}`.toUpperCase();
            return `<span style="
              display: inline-flex;
              align-items: center;
              vertical-align: middle;
              background-color: #1976d2;
              color: white;
              border-radius: 16px;
              padding: 2px 8px 2px 4px;
              margin: 0 2px;
              font-size: 0.8125rem;
              white-space: nowrap;
            ">
              <span style="
                width: 20px;
                height: 20px;
                border-radius: 50%;
                background-color: #1565c0;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                font-size: 10px;
                font-weight: bold;
                color: white;
                margin-right: 4px;
              ">${initials}</span>
              ${firstName} ${lastName}
            </span>`;
          });
          
          // Only allow safe HTML tags
          processedHtml = processedHtml.replace(/<(?!\/?(?:b|i|u|s|br|ul|ol|li|strong|em|span)(?:>|\s.*?>))[^>]*>/g, '');
          
          return (
            <Box
              key={index}
              component="div"
              sx={{
                '& ul, & ol': {
                  margin: '8px 0',
                  paddingLeft: '24px',
                },
                '& ul': {
                  listStyleType: 'disc',
                },
                '& ol': {
                  listStyleType: 'decimal',
                },
                '& li': {
                  marginBottom: '4px',
                  lineHeight: '1.5',
                },
              }}
              dangerouslySetInnerHTML={{ __html: processedHtml }}
            />
          );
        } else if (part.type === 'text') {
          // Check if the content has HTML formatting
          if (/<[^>]+>/.test(part.content)) {
            // Safely render HTML formatting tags
            return (
              <span 
                key={index} 
                dangerouslySetInnerHTML={{ 
                  __html: part.content
                    // Only allow specific formatting tags and list tags
                    .replace(/<(?!\/?(?:b|i|u|s|br|ul|ol|li)(?:>|\s.*?>))[^>]*>/g, '')
                }} 
              />
            );
          }
          return <span key={index}>{part.content}</span>;
        } else if (part.type === 'mention') {
          const user = mentionedUsers[part.username];
          const initials = part.username.split('_').map(name => name[0]).join('').toUpperCase();
          const displayName = user ? `${user.firstName} ${user.lastName}` : part.username.replace('_', ' ');
          
          return (
            <Tooltip
              key={index}
              title={user ? `${user.firstName} ${user.lastName}` : 'Loading...'}
              arrow
            >
              <MentionChipContainer
                onClick={() => handleMentionClick(part.username)}
              >
                <MentionAvatar>{initials}</MentionAvatar>
                <MentionLabel>{displayName}</MentionLabel>
              </MentionChipContainer>
            </Tooltip>
          );
        }
        return null;
      })}
    </Box>
  );
};

export default MentionText;